import type { ReactElement } from "react"

import CloseIcon from "@mui/icons-material/Close"
import { Drawer, type DrawerProps, Grid, IconButton } from "@mui/material"
import { Typography } from "@synapse-analytics/synapse-ui"

import styles from "./BaseDrawer.module.scss"

type BaseDrawerProps = {
  drawerProps?: DrawerProps
  title: string
  onClose?: () => void
  isCloseButtonDisabled?: boolean
  subHeader?: ReactElement
  footer?: ReactElement
  children?: ReactElement
}

/**
 * A reusable drawer component that slides in from the right side of the screen.
 *
 * It includes a title bar, an optional sub-header (e.g., for navigation or metadata),
 * and a scrollable content area for rendering children. A close button is provided in the header.
 *
 * @param drawerProps - Optional. Additional props to pass to the Material UI Drawer component.
 * @param title - The string displayed as the drawer's main title in the header.
 * @param onClose - A function called when the drawer requests to be closed (e.g., when clicking the backdrop or close button).
 * @param isCloseButtonDisabled - Optional. If `true`, disables the close button (e.g., to prevent closing during a loading state).
 * @param subHeader - Optional. A React element to render below the header (e.g., for navigation, metadata, or status).
 * @param footer - Optional. A React element to render at the bottom of the drawer.
 * @param children - The main content of the drawer, rendered in a scrollable area.
 *
 * @returns A right-anchored Material UI drawer component with header, optional sub-header, and scrollable content.
 */
export function BaseDrawer(props: Readonly<BaseDrawerProps>): ReactElement {
  const { drawerProps, onClose, title, isCloseButtonDisabled, subHeader, children, footer } = props

  return (
    <Drawer
      open={drawerProps?.open}
      onClose={drawerProps?.onClose}
      anchor="right"
      PaperProps={{ className: styles.drawerPaper, style: drawerProps?.PaperProps?.style }}
    >
      <Grid container display={"flex"} flexDirection={"column"} height={"100%"}>
        {/* Header section with title and close button */}
        <Grid item className={styles.drawerRunningSection}>
          <Typography variant="h3-bold">{title}</Typography>

          <IconButton onClick={onClose} size="small" className={"close-icon-button"} disabled={isCloseButtonDisabled}>
            <CloseIcon style={{ color: "var(--base-text-3)" }} />
          </IconButton>
        </Grid>

        {/* Sub-header section */}
        {subHeader && (
          <Grid item className={styles.drawerRunningSection}>
            {subHeader}
          </Grid>
        )}

        {/* Main content area */}
        <Grid item container className={styles.drawerContent}>
          {children}
        </Grid>

        {/* footer section */}
        {footer && (
          <Grid item className={styles.drawerRunningSection}>
            {footer}
          </Grid>
        )}
      </Grid>
    </Drawer>
  )
}
